import type { ContactInfo } from '@/features/appointments/utils/ContactInfo';
import useContactInfoPrefill from '@/hooks/useContactInfoPrefill';
import useSearchedDestinationLabel from '@/hooks/useSearchedDestinationLabel';
import useAppointmentStore from '@/store/AppointmentStore';
import {
  ContactForm,
  ContactFormBody,
  ContactFormGrid,
  ContactFormHeader,
} from './ContactForm/ContactFormLayout';
import {
  AdditionalInformationField,
  CommunicationPreferenceField,
  EmailField,
  ExpectedTravelTimeField,
  FirstNameField,
  LastNameField,
  PhoneNumberField,
  TypeOfTripField,
  ZipCodeField,
} from './ContactForm/fields';
import ProgressBar from './ProgressBar';
import TermsAndConditions from './TermsAndConditions';

export type ContactInfoFields = keyof ContactInfo;

interface ContactInfoFormProps {
  onSubmit: (data: unknown) => void;
}

function ContactInfoForm({ onSubmit }: ContactInfoFormProps) {
  const formValues = useAppointmentStore((state) => state.contactInfo);
  const updateField = useAppointmentStore((state) => state.patchContactInfo);
  const searchedDestinationLabel = useSearchedDestinationLabel();

  useContactInfoPrefill({
    formValues,
    updateField,
  });

  return (
    <ContactForm onSubmit={onSubmit}>
      <ContactFormBody>
        <ContactFormHeader
          header='Almost ready to start planning'
          subHeader={
            searchedDestinationLabel
              ? `Share a few details so your ${searchedDestinationLabel} advisor can prep before your appointment.`
              : 'Share a few details so your advisor can prep before your appointment.'
          }
        />
        <ContactFormGrid>
          <FirstNameField
            value={formValues.firstName}
            onChange={(v) => updateField({ firstName: v })}
            required
          />
          <LastNameField
            value={formValues.lastName}
            onChange={(v) => updateField({ lastName: v })}
            required
          />
          <ZipCodeField
            value={formValues.zipCode}
            onChange={(v) => updateField({ zipCode: v })}
            required
          />
          <CommunicationPreferenceField
            value={formValues.communicationPreference}
            onChange={(v) =>
              updateField({
                communicationPreference: v as any,
              })
            }
            required
          />
          <PhoneNumberField
            value={formValues.phoneNumber}
            onChange={(v) => updateField({ phoneNumber: v })}
            required
          />
          <EmailField
            value={formValues.email}
            onChange={(v) => updateField({ email: v })}
            required
          />
          <ExpectedTravelTimeField
            value={formValues.expectedTravelTime}
            onChange={(v) => updateField({ expectedTravelTime: v })}
          />
          <TypeOfTripField
            value={formValues.typeOfTrip}
            onChange={(v) => updateField({ typeOfTrip: v })}
            required
          />
          <AdditionalInformationField
            value={formValues.additionalInformation}
            onChange={(v) => updateField({ additionalInformation: v })}
          />
        </ContactFormGrid>
        <TermsAndConditions buttonLabel='Book now' />
      </ContactFormBody>
      <ProgressBar
        nextProgressValue={75}
        displayBackButton
        isInsideForm
        fixed
        continueButtonLabel='Book now'
      />
    </ContactForm>
  );
}

export default ContactInfoForm;
